import React from "react";
import {OverlayTrigger, Table, Tooltip} from "react-bootstrap";
import HeaderWithHelp from "../SalesAnalysis/HeaderWithHelp.jsx";
import {formatDatum, formatDatumZeit} from "./format.js";
import {TOOLTIP_POPPER_CONFIG} from "./tooltipConfig.js";

const ALTER_BADGE = {
    frisch: {klasse: "text-bg-success", label: "frisch", hilfe: "Der Stand ist so aktuell, wie es der übliche Rhythmus dieser Quelle erwarten lässt."},
    aelter: {klasse: "text-bg-warning", label: "älter", hilfe: "Der Stand liegt spürbar hinter dem üblichen Rhythmus zurück, ist aber noch kein Ausfall."},
    veraltet: {klasse: "text-bg-danger", label: "veraltet", hilfe: "Der Stand liegt deutlich hinter dem üblichen Rhythmus zurück – der zuständige Lauf sollte geprüft werden."},
    unbekannt: {klasse: "text-bg-secondary", label: "unbekannt", hilfe: "Für diese Quelle liegt noch kein Stand vor."},
};

const ZELLE_UMBRUCH = {whiteSpace: "normal", wordBreak: "break-word"};

/**
 * Kennzahlen-Tabelle der Seite "Datenquellen" (PLAN.md Abschnitt 4.6): je
 * Zeile Aussage, Quelle, Stand (mit Alters-Badge), Rhythmus, Verlässlichkeit
 * und Rechenweg. table-layout: fixed mit festen Prozent-Breiten, damit die
 * Tabelle bei 1440 px ohne waagerechtes Scrollen passt (Sichtprüfung 1,
 * Befund 25) - lange Texte (Rechenweg, Quelle) brechen dafür um, statt die
 * Spalte über den Bildschirmrand hinaus zu ziehen.
 */
export default function DatenquellenTabelle({kennzahlen}) {
    if (!kennzahlen || kennzahlen.length === 0) {
        return null;
    }

    return (
        <Table responsive size="sm" className="align-middle" style={{tableLayout: "fixed", width: "100%"}}>
            <thead>
                <tr>
                    <th style={{width: "19%"}}>
                        <HeaderWithHelp help="Um welche Kennzahl es geht und was sie aussagt.">Kennzahl</HeaderWithHelp>
                    </th>
                    <th style={{width: "19%"}}>
                        <HeaderWithHelp help="Woher der Wert stammt.">Quelle</HeaderWithHelp>
                    </th>
                    <th style={{width: "14%"}}>
                        <HeaderWithHelp help="Zeitpunkt des jüngsten zugrunde liegenden Datensatzes, live aus der Datenbank ermittelt.">Stand</HeaderWithHelp>
                    </th>
                    <th style={{width: "13%"}}>
                        <HeaderWithHelp help="Wie oft diese Quelle automatisch erneuert wird.">Rhythmus</HeaderWithHelp>
                    </th>
                    <th style={{width: "13%"}}>
                        <HeaderWithHelp help="Wie verlässlich der Wert ist – fest gemessen oder (noch) mit Vorbehalt.">Verlässlichkeit</HeaderWithHelp>
                    </th>
                    <th style={{width: "22%"}}>
                        <HeaderWithHelp help="Wie der Wert berechnet wird, in Klartext.">Rechenweg</HeaderWithHelp>
                    </th>
                </tr>
            </thead>
            <tbody>
                {kennzahlen.map((k) => {
                    const badge = ALTER_BADGE[k.alter_stufe] || ALTER_BADGE.unbekannt;

                    return (
                        <tr key={k.schluessel}>
                            <td style={ZELLE_UMBRUCH}>
                                <div className="fw-semibold">{k.titel}</div>
                                <div className="small text-muted">{k.aussage}</div>
                            </td>
                            <td className="small text-muted" style={ZELLE_UMBRUCH}>
                                {k.quelle}
                            </td>
                            <td style={ZELLE_UMBRUCH}>
                                <OverlayTrigger flip popperConfig={TOOLTIP_POPPER_CONFIG} placement="top" overlay={<Tooltip id={`cosmos-alter-${k.schluessel}`}>{badge.hilfe}</Tooltip>}>
                                    <span className={`badge ${badge.klasse} me-2`}>{badge.label}</span>
                                </OverlayTrigger>
                                <span className="small">{(typeof k.stand === "string" && k.stand.includes("T00:00:00") ? formatDatum(k.stand) : formatDatumZeit(k.stand)) || "–"}</span>
                            </td>
                            <td className="small" style={ZELLE_UMBRUCH}>
                                {k.aktualisierung ?? k.rhythmus}
                            </td>
                            <td className="small" style={ZELLE_UMBRUCH}>
                                {k.verlaesslichkeit}
                            </td>
                            <td className="small text-muted" style={ZELLE_UMBRUCH}>
                                {k.rechenweg}
                            </td>
                        </tr>
                    );
                })}
            </tbody>
        </Table>
    );
}
